import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Play, Pause, Square, RotateCcw, Clock } from 'lucide-react';
import { getSportConfig } from '@/config/sportsConfig';

interface GameClockTimerProps {
  sportName: string;
  currentPeriod: number;
  onClockUpdate: (timeRemaining: number, isRunning: boolean) => void;
  onPeriodChange: (period: number) => void;
}

const GameClockTimer = ({ sportName, currentPeriod, onClockUpdate, onPeriodChange }: GameClockTimerProps) => {
  const config = getSportConfig(sportName);
  
  const [timeRemaining, setTimeRemaining] = useState(config.periodLength);
  const [isRunning, setIsRunning] = useState(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(Math.abs(seconds) / 60);
    const secs = Math.abs(seconds) % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Update parent component
  useEffect(() => {
    onClockUpdate(timeRemaining, isRunning);
  }, [timeRemaining, isRunning, onClockUpdate]);

  // Timer logic
  useEffect(() => {
    if (isRunning && config.periodLength > 0) {
      const id = setInterval(() => {
        setTimeRemaining(prev => {
          if (config.countDown) {
            const newTime = prev - 1;
            if (newTime <= 0) {
              setIsRunning(false);
              return 0;
            }
            return newTime;
          } else {
            return prev + 1;
          }
        });
      }, 1000);
      
      setIntervalId(id);
      return () => clearInterval(id);
    } else if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [isRunning, config.countDown, config.periodLength]);

  const startClock = () => setIsRunning(true);
  const pauseClock = () => setIsRunning(false);
  const stopClock = () => {
    setIsRunning(false);
    setTimeRemaining(config.periodLength);
  };

  const resetClock = () => {
    setIsRunning(false);
    setTimeRemaining(config.periodLength);
  };

  const handlePeriodChange = (newPeriod: string) => {
    const periodNum = parseInt(newPeriod);
    onPeriodChange(periodNum);
    setIsRunning(false);
    setTimeRemaining(config.periodLength);
  };

  return (
    <div className="space-y-4">
      {/* Period Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          {config.periods === 1 ? config.periodType : config.periodType}
        </label>
        <Select value={currentPeriod.toString()} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {config.periodNames.map((name, index) => (
              <SelectItem key={index + 1} value={(index + 1).toString()}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Clock Display */}
      <div className="text-center space-y-2">
        <div className="text-3xl font-mono font-bold text-foreground">
          {config.periodLength > 0 ? formatTime(timeRemaining) : '--:--'}
        </div>
        <Badge variant={isRunning ? "default" : "secondary"}>
          <Clock className="h-3 w-3 mr-1" />
          {isRunning ? 'Running' : 'Stopped'}
        </Badge>
      </div>

      {/* Clock Controls */}
      {config.periodLength > 0 && (
        <div className="flex justify-center gap-3">
          {!isRunning ? (
            <Button size="sm" onClick={startClock} variant="default">
              <Play className="h-4 w-4" />
            </Button>
          ) : (
            <Button size="sm" onClick={pauseClock} variant="secondary">
              <Pause className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" onClick={resetClock} variant="outline">
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Sport Info */}
      <div className="text-center">
        <Badge variant="outline" className="text-xs">
          {sportName} - {config.periods} {config.periods === 1 ? 'Period' : 'Periods'}
        </Badge>
      </div>
    </div>
  );
};

export default GameClockTimer;